<template>
  <CatNicknameDialogBase :on-click-outside="onClickOutside">
    <template #header>
      <div class="dialog-header">
        <div class="cat-display">
          <cat-icon size="large" :clickable="false" :writer-type="assistantCat?.assistantCatType" />
        </div>
        <h2 class="dialog-title">{{ $t("myPage.catNicknameConfirmDialog.title") }}</h2>
        <p class="dialog-subtitle">
          {{ dialogSubtitle }}
        </p>
      </div>
    </template>

    <template #footer>
      <div class="dialog-footer">
        <button class="button cancel" @click="onClose">{{ $t("common.close") }}</button>
        <div class="divider"></div>
        <button class="button primary" @click="onConfirm">{{ $t("common.yes") }}</button>
      </div>
    </template>
  </CatNicknameDialogBase>
</template>

<script lang="ts">
import Vue from "vue";
import CatIcon from "@/components/atoms/CatIcon.vue";
import CatNicknameDialogBase from "@/components/ui/CatNicknameDialogBase.vue";
import { AssistantCat } from "@/lib/models/assistantCat";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    CatIcon,
    CatNicknameDialogBase,
  },

  props: {
    assistantCat: {
      type: Object as () => AssistantCat | null,
      default: null,
    },
    nickname: {
      type: String,
      required: true,
    },
    isResettingToDefault: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    dialogSubtitle(): string {
      return this.isResettingToDefault
        ? (this.$t("myPage.catNicknameConfirmDialog.subtitleReset") as string)
        : (this.$t("myPage.catNicknameConfirmDialog.subtitleSet", { nickname: this.nickname }) as string);
    },
  },

  methods: {
    onClose() {
      this.$emit("close");
    },

    onConfirm() {
      this.$emit("confirm");
    },

    onClickOutside() {
      this.$emit("close");
    },
  },
});

interface Data {}

interface Methods {
  onClose: () => void;
  onConfirm: () => void;
  onClickOutside: () => void;
}

interface Computed {
  dialogSubtitle: string;
}

interface Props {
  assistantCat: AssistantCat | null;
  nickname: string;
  isResettingToDefault: boolean;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.dialog-header {
  text-align: center;
  margin-bottom: 20px;

  .cat-display {
    margin-bottom: 15px;
    display: flex;
    justify-content: center;
  }

  .dialog-title {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    margin-bottom: 8px;
    color: var(--color-text-normal);
  }

  .dialog-subtitle {
    font-size: var(--font-size-sm);
    color: var(--color-text-low);
    margin: 0;
    line-height: 1.5;
  }
}

.dialog-footer {
  margin: 20px -20px 0 -20px;
  border-top: 1px solid var(--color-border);
  display: flex;
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
  overflow: hidden;

  .button {
    flex: 1;
    height: 36px;
    border: none;
    background-color: transparent;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &.cancel {
      color: var(--color-text-low);
    }

    &.primary {
      color: var(--color-primary);
    }
  }

  .divider {
    width: 1px;
    align-self: stretch;
    background-color: var(--color-border);
    flex-shrink: 0;
  }
}

@media (max-width: 576px) {
  .dialog-footer {
    margin: 15px -15px 0 -15px;
  }
}
</style>
